import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconButton } from './Button';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
}

/**
 * A bottom sheet on phones and a centred panel on wide screens, built on the native <dialog>,
 * which brings focus trapping, Escape to close and an inert background. It is the one surface
 * that floats, so it is the one that casts a shadow.
 */
export function Sheet({ open, onClose, title, description, children }: SheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
      className="sheet fixed inset-x-0 top-auto bottom-0 m-0 mx-auto max-h-[90svh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-surface p-0 text-ink shadow-2xl backdrop:bg-scrim sm:top-0 sm:bottom-0 sm:my-auto sm:h-fit sm:rounded-2xl"
    >
      <div className="px-5 pt-3 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:px-6 sm:pt-5">
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-raised sm:hidden" aria-hidden />
        <div className="mb-4 flex items-start justify-between gap-4">
          <div className="min-w-0 pt-1.5">
            <h2 id={titleId} className="heading text-lead">
              {title}
            </h2>
            {description && <p className="mt-0.5 text-small text-muted">{description}</p>}
          </div>
          <IconButton label="Close" onClick={onClose} className="-mr-2">
            <X />
          </IconButton>
        </div>
        {open && children}
      </div>
    </dialog>
  );
}
